import React, { useEffect, useMemo, useRef, useCallback } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import InsightPanel from "../InsightPanel/InsightPanel";
import UIConfig from "../../constants/UIConfig";
import magicHealthLogo from "../../../assets/images/magicHealthLogo.png";
import { renderCallReviewAutoTables } from "./generateCallReviewAutoTable";

// How long we'll wait for the offscreen panel's async content (e.g. the Call
// Review Sheet fetch that only fires for Community Origin Lead Handling
// calls) to settle before capturing anyway. Prevents a stalled network call
// from hanging the download forever.
const CONTENT_READY_TIMEOUT_MS = 15000;
const CONTENT_READY_POLL_MS = 200;
// Small settle delay after content is ready, to let the final layout/paint
// (images, reflow) catch up before html2canvas snapshots the DOM.
const CAPTURE_SETTLE_DELAY_MS = 350;

// Scans upward from the ideal (blind) page-break line for a horizontal band
// of near-white canvas pixels — i.e. the gap between table rows/sections —
// so pages break between content instead of slicing through the middle of a
// row or a line of text. Falls back to the ideal line if nothing clean is
// found nearby (better to occasionally cut than to search forever or shrink
// pages down to nothing).
const MAX_BREAK_SEARCH_PX = 260;
const WHITE_THRESHOLD = 245;
const SAMPLE_STRIDE_PX = 6;

const findSafePageBreak = (canvas, idealCutY, minY) => {
  if (idealCutY >= canvas.height) return idealCutY;

  const top = Math.max(minY, Math.floor(idealCutY - MAX_BREAK_SEARCH_PX));
  const bandHeight = Math.floor(idealCutY) - top;
  if (bandHeight <= 0) return idealCutY;

  let imageData;
  try {
    imageData = canvas.getContext("2d").getImageData(0, top, canvas.width, bandHeight);
  } catch (err) {
    // Tainted canvas (cross-origin image without CORS) — can't inspect
    // pixels, just use the blind cut line.
    return idealCutY;
  }

  const { data, width } = imageData;
  for (let row = bandHeight - 1; row >= 0; row--) {
    let isBlankRow = true;
    const rowOffset = row * width * 4;
    for (let x = 0; x < width; x += SAMPLE_STRIDE_PX) {
      const idx = rowOffset + x * 4;
      if (
        data[idx] < WHITE_THRESHOLD ||
        data[idx + 1] < WHITE_THRESHOLD ||
        data[idx + 2] < WHITE_THRESHOLD
      ) {
        isBlankRow = false;
        break;
      }
    }
    if (isBlankRow) return top + row;
  }

  return idealCutY;
};

const loadImageSafe = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => resolve(img);
    // Never let a broken/slow logo hang PDF generation — just skip it.
    img.onerror = () => resolve(null);
    img.src = src;
  });

const InsightPDFDownload = ({
  triggerDownload,
  setTriggerDownload,
  memberId,
  callId,
  onLoadingChange,
}) => {
  const insightRef = useRef(null);
  const panelReadyRef = useRef(true);
  const callReviewPayloadRef = useRef(null);
  const memberDetails = useSelector(
    (state) => state.operationHome.details.data
  );

  // Derived synchronously (not via useEffect+state) so it's correct on the
  // very first render — avoids a stale-null race that would otherwise make
  // download validation misfire immediately after the component mounts.
  const selectedSection = useMemo(() => {
    if (!memberDetails?.callDetails || !callId) return null;

    const callsByCallId = memberDetails.callDetails.reduce((acc, current) => {
      const id = current.CallId;
      if (!acc[id]) {
        acc[id] = {
          CallId: id,
          meetingDate: current.meetingDate,
          meetingPerson: current.meetingPerson,
          duration: current.duration,
          followUpRequired: current.followUpRequired,
          items: [],
        };
      }
      acc[id].items.push(current);
      return acc;
    }, {});

    return callsByCallId[callId] || null;
  }, [memberDetails, callId]);

  const notifyLoading = useCallback(
    (isLoading) => {
      if (typeof onLoadingChange === "function") onLoadingChange(isLoading);
    },
    [onLoadingChange]
  );

  useEffect(() => {
    if (!triggerDownload) return;

    // Validate required data up front rather than silently doing nothing —
    // surface a real error so the user isn't left staring at a stuck button.
    if (!memberDetails || !selectedSection) {
      toast.error("Unable to generate the PDF: call details are not available.");
      setTriggerDownload(false);
      return;
    }

    if (!insightRef.current) return;

    let cancelled = false;

    const generatePDF = async () => {
      try {
        const pdf = new jsPDF("p", "mm", "a4", true);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        // Constants for Layout
        const headerHeight = 50;
        const footerHeight = 10;
        const marginSmall = 10; // For pages 2+

        const canvas = await html2canvas(insightRef.current, {
          useCORS: true,
          scale: 2,
          logging: false,
          allowTaint: true,
        });

        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        if (!canvasWidth || !canvasHeight) {
          throw new Error("Insight content could not be captured (empty canvas)");
        }

        // Prepare Images — failures are tolerated, logos are optional.
        const [clientLogo, mHealthLogo] = await Promise.all([
          loadImageSafe(UIConfig?.branding?.logo),
          loadImageSafe(magicHealthLogo),
        ]);

        if (cancelled) return;

        let positionY = 0;
        let pageNum = 1;

        while (positionY < canvasHeight) {
          if (pageNum > 1) pdf.addPage();

          // --- SECTION 1: HEADER (ONLY PAGE 1) ---
          if (pageNum === 1) {
            // White Header background
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pdfWidth, headerHeight, "F");

            // Logos
            if (clientLogo) {
              const cLogoH = 8;
              const cLogoW = (clientLogo.width / clientLogo.height) * cLogoH;
              pdf.addImage(clientLogo, "PNG", 15, 10, cLogoW, cLogoH);
            }

            pdf.setFontSize(9);
            pdf.setTextColor(150);
            pdf.text("Powered by", pdfWidth - 54, 14, { align: "right" });
            if (mHealthLogo) {
              const mLogoH = 6;
              const mLogoW = (mHealthLogo.width / mHealthLogo.height) * mLogoH;
              pdf.addImage(mHealthLogo, "PNG", pdfWidth - 15 - mLogoW, 9.5, mLogoW, mLogoH);
            }

            // Details Grid
            pdf.setDrawColor(240);
            pdf.line(15, 22, pdfWidth - 15, 22);
            pdf.setFontSize(8);
            pdf.setTextColor(100);
            pdf.setFont("helvetica", "bold");

            const col1 = 15, col2 = 80, col3 = 145;
            const row1 = 30, row2 = 40;

            pdf.text("MEMBER NAME", col1, row1);
            pdf.text("CUSTOMER ID", col2, row1);
            pdf.text("ASSIGNED AGENT", col3, row1);
            pdf.text("MEETING DATE", col1, row2);
            pdf.text("CALL DURATION", col2, row2);
            pdf.text("FOLLOW-UP", col3, row2);

            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(50);
            pdf.setFontSize(10);
            pdf.text(`${memberDetails?.FirstName || ""} ${memberDetails?.LastName || ""}`.trim() || "N/A", col1, row1 + 5);
            pdf.text(`${memberDetails?.CustomerID || "---"}`, col2, row1 + 5);
            pdf.text(`${selectedSection?.meetingPerson || "---"}`, col3, row1 + 5);
            const meetingDateRaw = selectedSection?.meetingDate;
            let meetingDateStr = "---";
            if (meetingDateRaw) {
              const parsedDate = new Date(meetingDateRaw);
              if (!isNaN(parsedDate.getTime())) {
                meetingDateStr = parsedDate.toLocaleDateString("en-US", {
                  timeZone: UIConfig.branding.timeZone || "America/New_York",
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                });
              }
            }
            pdf.text(meetingDateStr, col1, row2 + 5);

            pdf.text(`${selectedSection?.duration || "---"}`, col2, row2 + 5);
            pdf.text(`${selectedSection?.followUpRequired ? "Required" : "Not Required"}`, col3, row2 + 5);
          }

          // --- SECTION 2: DYNAMIC CONTENT CALCULATION ---
          const currentContentTop = pageNum === 1 ? headerHeight : marginSmall;
          const currentAvailableHeight = pdfHeight - currentContentTop - footerHeight;

          // Calculate how many pixels of the canvas fit into this page's available height
          const pxPerPage = (canvasWidth * currentAvailableHeight) / (pdfWidth - 6);

          // Nudge the break point up to the nearest blank (whitespace) band
          // near the ideal cut line so a page break never slices through the
          // middle of a table row or line of text.
          const idealCutY = Math.min(positionY + pxPerPage, canvasHeight);
          const cutY =
            idealCutY >= canvasHeight
              ? idealCutY
              : findSafePageBreak(canvas, idealCutY, positionY);
          const sliceHeight = Math.max(1, cutY - positionY);

          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvasWidth;
          pageCanvas.height = sliceHeight;

          const ctx = pageCanvas.getContext("2d");
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, canvasWidth, pageCanvas.height);
          ctx.drawImage(canvas, 0, positionY, canvasWidth, pageCanvas.height, 0, 0, canvasWidth, pageCanvas.height);

          const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.8);
          const contentHeightMM = (pageCanvas.height * (pdfWidth - 6)) / canvasWidth;

          // Blue Sidebar Stripe — sized to the actual content on this page,
          // not the full page slot, so it doesn't run into trailing
          // whitespace left by the safe-break adjustment above.
          pdf.setFillColor(30, 60, 114);
          pdf.rect(0, currentContentTop, 4, Math.min(contentHeightMM, currentAvailableHeight), "F");

          // Add the Insight Content
          pdf.addImage(
            pageImgData,
            "JPEG",
            6,
            currentContentTop,
            pdfWidth - 6,
            contentHeightMM,
            undefined,
            'FAST'
          );

          // Footer
          pdf.setFontSize(8);
          pdf.setTextColor(180);
          pdf.text(`${pageNum}`, pdfWidth / 2, pdfHeight - 5, { align: "center" });

          positionY += sliceHeight;
          pageNum++;
        }

        if (cancelled) return;

        // The Call Review scoring tables (Community Origin Lead Handling
        // calls only) are rendered natively via jsPDF-AutoTable rather than
        // captured as a screenshot — see generateCallReviewAutoTable.js.
        // AutoTable handles page-break-safe rows, repeated headers and
        // column fitting on its own, which a raster capture cannot.
        if (callReviewPayloadRef.current?.callReviewData) {
          const tableMargin = 14;
          const firstTablePage = pdf.internal.getNumberOfPages() + 1;

          pdf.addPage();
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(13);
          pdf.setTextColor(30, 60, 114);
          pdf.text("Call Review & Scoring", tableMargin, 20);
          pdf.setDrawColor(220);
          pdf.setLineWidth(0.3);
          pdf.line(tableMargin, 24, pdfWidth - tableMargin, 24);

          renderCallReviewAutoTables({
            doc: pdf,
            startY: 30,
            margin: tableMargin,
            callReviewData: callReviewPayloadRef.current.callReviewData,
          });

          // Stamp footer page numbers across however many pages the tables
          // ended up spanning, continuing the running count from above —
          // AutoTable may have added any number of pages internally.
          const lastTablePage = pdf.internal.getNumberOfPages();
          for (let p = firstTablePage; p <= lastTablePage; p++) {
            pdf.setPage(p);
            pdf.setFontSize(8);
            pdf.setTextColor(180);
            pdf.text(`${pageNum}`, pdfWidth / 2, pdfHeight - 5, { align: "center" });
            pageNum++;
          }
        }

        if (cancelled) return;

        const safeMemberId = (memberId || "report").toString().replace(/[^a-z0-9-_]/gi, "_");
        pdf.save(`Insight_Report_${safeMemberId}.pdf`);
      } catch (err) {
        console.error("Insight PDF generation failed:", err);
        if (!cancelled) {
          toast.error("Unable to generate the PDF. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setTriggerDownload(false);
          notifyLoading(false);
        }
      }
    };

    notifyLoading(true);

    // Wait for the panel's async content (e.g. Call Review Sheet fetch, which
    // only applies to Community Origin Lead Handling calls) to finish
    // rendering — instead of a blind fixed delay — before snapshotting it.
    // This is what previously produced a blank/incomplete PDF for those
    // calls: html2canvas fired before the extra fetch had resolved.
    let waited = 0;
    let settleTimer;
    const waitForContentReady = () => {
      if (cancelled) return;
      if (panelReadyRef.current || waited >= CONTENT_READY_TIMEOUT_MS) {
        settleTimer = setTimeout(generatePDF, CAPTURE_SETTLE_DELAY_MS);
      } else {
        waited += CONTENT_READY_POLL_MS;
        settleTimer = setTimeout(waitForContentReady, CONTENT_READY_POLL_MS);
      }
    };
    waitForContentReady();

    return () => {
      cancelled = true;
      clearTimeout(settleTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerDownload, selectedSection, memberDetails, memberId]);

  if (!memberDetails || !selectedSection) return null;

  return (
    <div style={{ position: "absolute", left: "-9999px", top: 0, zIndex: -1 }}>
      <div
        ref={insightRef}
        className="pdf-capture"
        style={{
          width: "900px",
          padding: "20px 40px",
          background: "#fff"
        }}
      >
        <InsightPanel
          memberId={memberId}
          memberDetails={memberDetails}
          section={selectedSection}
          callId={callId}
          forceExpand={true}
          onReadyChange={(ready) => {
            panelReadyRef.current = ready;
          }}
          onCallReviewData={(payload) => {
            callReviewPayloadRef.current = payload;
          }}
        />
      </div>
    </div>
  );
};

export default InsightPDFDownload;
